import React, { useCallback, useState } from 'react';
import { ORDER_BY_DIRECTION_ASC } from '@atlaskit/jql-ast';
import type { TriggerProps } from '@atlaskit/popup';
import { Flex } from '@atlaskit/primitives';
import { useIntl } from '@atlassian/jira-intl';
import type {
	AsyncLoadOptions,
	OnChangeWithData,
	SelectValue,
} from '@atlassian/jira-jql-builder-basic-picker/src/common/types.tsx';
import { PickerEntryPoint } from '@atlassian/jira-jql-builder-basic-picker/src/ui/PickerEntryPoint.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { EntryPointButtonTrigger } from '@atlassian/jira-entry-point-button-trigger/src/index.tsx';
import PickerTrigger from '../../common/ui/picker-trigger/index.tsx';
import type { OrderByField } from '../../types.tsx';
import messages from './messages.tsx';

const FIELD_ID = 'add-order-by-field';

const VALUE: SelectValue = [];

type Props = {
	onLoadOptions: AsyncLoadOptions;
	onSelectField: (field: OrderByField) => void;
};

export const AddFieldPicker = ({ onLoadOptions, onSelectField }: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const [isOpen, setIsOpen] = useState(false);

	const handleTriggerClick = useCallback(
		(e: React.MouseEvent | React.KeyboardEvent) => {
			e.preventDefault();

			setIsOpen((currentisOpen) => {
				const newOpenState = !currentisOpen;

				fireUIAnalytics(createAnalyticsEvent({}), {
					action: 'clicked',
					actionSubject: 'dropdownMenu',
					actionSubjectId: 'addOrderByFieldPicker',
					attributes: {
						isOpen: newOpenState,
					},
				});

				return newOpenState;
			});
		},
		[createAnalyticsEvent],
	);

	const handlePickerCloseClick = useCallback(
		(
			e:
				| Event
				| KeyboardEvent
				| React.MouseEvent<HTMLElement>
				| React.KeyboardEvent<HTMLDivElement>,
		) => {
			e.preventDefault();
			setIsOpen(false);
		},
		[],
	);

	const handleFieldChanged: OnChangeWithData = useCallback(
		(newValue, actionMeta) => {
			// @ts-expect-error TS inference not working with the SelectedValue type
			// despite explicit checks it always still thinks it might be an array or null
			// not sure of the root cause whether type defs in
			// jira/src/packages/jql-builder/basic-picker/src/common/types.tsx or TS issue at
			// the platform level
			if (newValue === null || Array.isArray(newValue) || !newValue.value || !newValue.label) {
				return null;
			}

			if (actionMeta.action === 'select-option') {
				const updatedField: OrderByField = {
					// @ts-expect-error see above
					displayText: newValue.label,
					// @ts-expect-error see above
					jqlTerm: newValue.value,
					direction: ORDER_BY_DIRECTION_ASC,
				};

				onSelectField(updatedField);
				setIsOpen(false);

				fireUIAnalytics(
					createAnalyticsEvent({
						action: 'changed',
						actionSubject: 'selectOption',
					}),
					'addOrderByFieldPicker',
				);
			}
		},
		[createAnalyticsEvent, onSelectField],
	);

	const onPickerTrigger = useCallback(
		(triggerProps: TriggerProps, triggerRef: EntryPointButtonTrigger) => (
			<PickerTrigger
				ariaLabel={formatMessage(messages.addFieldDisplayText)}
				triggerProps={triggerProps}
				displayText={formatMessage(messages.addFieldDisplayText)}
				onClick={handleTriggerClick}
				isOpen={isOpen}
				triggerRef={triggerRef}
			/>
		),
		[formatMessage, handleTriggerClick, isOpen],
	);

	return (
		<Flex>
			<PickerEntryPoint
				isMulti={false}
				isOpen={isOpen}
				id={FIELD_ID}
				value={VALUE}
				onTrigger={onPickerTrigger}
				onClose={handlePickerCloseClick}
				onChange={handleFieldChanged}
				onLoadOptions={onLoadOptions}
				shouldRenderToParent
			/>
		</Flex>
	);
};
